import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { StorageService } from 'src/app/services/configurators-2d/applications/storage.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { StorageConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/storage-configuration-data';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { PricingState } from 'src/app/stores/tunnel/model/pricing-state';
import { FundingState } from 'src/app/stores/tunnel/model/funding-state';
import { Funding } from 'src/app/stores/tunnel/model/funding';
import { LiteOfferResult } from 'src/app/models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { InputNumber } from 'primeng/inputnumber';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import { isFundingEmpty, isFundingStateEmpty } from 'src/app/services/helpers/funding.helper';

type Column = {
  header: string;
  field: string;
};

@Component({
  selector: 'ng-casto-storage-funding',
  templateUrl: './storage-funding.component.html',
})
export class StorageFundingComponent extends BaseTunnelObject implements OnInit, OnDestroy {
  protected currentPricing: PricingState;
  protected currentFunding: FundingState;
  protected currentDiscount: DiscountTypeEnum = null;
  protected currentWithInstallation: boolean = null;
  protected loading: boolean = false;
  protected pricingLoading: boolean = false;
  protected downPayment: number = 0;
  private storageConfiguration: StorageConfigurationData;

  protected readonly offersColumns: Column[] = [
    { header: 'Barème', field: 'code' },
    { header: 'Offre', field: 'offer' },
    { header: 'Mensualité', field: 'mensuality' },
    { header: 'Durée', field: 'duration' },
    { header: 'Total intérêts', field: 'interest' },
    { header: 'Total dû', field: 'total' },
    { header: 'TAEG', field: 'taeg' },
  ];

  constructor(
    protected tunnelDataStore: TunnelDataStore,
    protected tunnelNavigationStore: TunnelNavigationStore,
    protected storageService: StorageService,
    private readonly statisticService: StatisticService,
    protected featureSettingsService: FeatureSettingsService
  ) {
    super(tunnelNavigationStore, tunnelDataStore);
  }

  get offers(): LiteOfferResult[] {
    if (!this.currentFunding) {
      return [];
    }
    return this.funding.offerTable;
  }

  get funding(): Funding {
    if (this.currentFunding) {
      return this.currentFunding[
        this.currentWithInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'
      ];
    }
  }

  get offersWithFooter() {
    return this.offers.length > 0 ? [...this.offers, { isTextContainer: true }] : [];
  }

  ngOnInit(): void {
    super.ngOnInit();
    void this.initFundingStep();
  }

  private async initFundingStep(): Promise<void> {
    // Restaure l'apport persisté avant un éventuel recalcul : `computeFunding` envoie
    // `this.downPayment`, et l'API n'utilise l'apport enregistré que si le paramètre est nul —
    // le lire après coup écraserait donc l'apport du vendeur par 0.
    this.downPayment = this.currentFunding?.downPayment ?? 0;
    await this.initFundingIfNeeded();
    this.downPayment = this.currentFunding?.downPayment ?? this.downPayment;
    await this.sendAccessFundingStatistic();
  }

  async sendAccessFundingStatistic() {
    //Envoi de la stat d'accès à la page de simulation de financement
    this.statisticService.sendStatistic(
      StatisticTypeEnum.FUNDING_COUNT,
      1,
      CONFIG_CONTEXT.STORAGE,
      this.storageConfiguration.configurationId
    );
  }

  protected isNoOffer(): boolean {
    return isFundingEmpty(this.funding);
  }

  get fundingAmount(): number {
    return Math.max(this.currentPricing.totalWithoutFunding.actualAmount - (this.downPayment ?? 0), 0);
  }

  get displayDiscount(): boolean {
    return this.discountNumberValue > 0;
  }

  get discountNumberValue(): number {
    switch (this.currentDiscount) {
      case DiscountTypeEnum.Discount0:
        return 0;
      case DiscountTypeEnum.Discount5:
        return 5;
      case DiscountTypeEnum.Discount10:
        return 10;
      case DiscountTypeEnum.Discount20:
        return 20;
    }
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(currentFunding => {
        if (currentFunding) {
          this.currentFunding = currentFunding;
        }
      }),
      this.tunnelDataStore.getCurrentWithInstallation().subscribe(async currentWithInstallation => {
        if (currentWithInstallation !== undefined && this.currentWithInstallation !== currentWithInstallation) {
          let lastCurrentValue = this.currentWithInstallation;
          this.currentWithInstallation = currentWithInstallation;
          //Nathan - 08/07 : Ces lignes étaient commentées par Benjamin, surement pour essayer d'opti les appels
          if (lastCurrentValue !== null) {
            await this.updateFunding();
          }
        }
      }),
      this.tunnelDataStore.getCurrentDiscount().subscribe(async currentDiscount => {
        if (currentDiscount && this.currentDiscount !== currentDiscount) {
          this.currentDiscount = currentDiscount;
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.storageConfiguration = configurationData.get(CONFIG_CONTEXT.STORAGE) as StorageConfigurationData;
        }
      }),
      this.tunnelDataStore.getPricingLoading().subscribe(loading => {
        this.pricingLoading = loading;
      })
    );
  }

  private async computeFunding() {
    await this.storageService.computeFunding({
      tunnelConfigurationId: this.storageConfiguration.configurationId,
      downPaymentAmount: this.downPayment,
      shouldRecalculateFinancing: true,
    });
  }

  protected async updateFunding() {
    try {
      this.loading = true;
      const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

      if (!financementEnabled) {
        await this.ensurePricingIfNeeded();
        this.tunnelDataStore.disableCurrentFunding();
        this.checkDownPayment();
        return;
      }

      await this.computeFunding();
      this.checkDownPayment();
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }

  private async initFundingIfNeeded() {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      await this.ensurePricingIfNeeded();
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    // Valeur autoritaire du store : `this.currentFunding` n'est pas remis à `undefined` par
    // `resetCurrentFunding()`, la souscription filtrant sur `if (funding)`.
    if (isFundingStateEmpty(this.tunnelDataStore.getCurrentStateValue().currentFunding)) {
      await this.updateFunding();
    }
  }

  private async ensurePricingIfNeeded(): Promise<void> {
    if (!this.currentPricing?.totalWithoutFunding) {
      await this.storageService.loadStoragePricing(this.storageConfiguration.configurationId);
    }
  }

  private checkDownPayment() {
    const totalWithoutFunding = this.currentPricing?.totalWithoutFunding?.actualAmount ?? 0;
    if (this.downPayment > totalWithoutFunding) {
      this.downPayment = totalWithoutFunding;
    }
  }

  protected async setWithInstallation(withInstallation: boolean) {
    try {
      this.loading = true;
      await this.storageService.updateWithInstallation(withInstallation);
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  protected isOfferSelected(offer: LiteOfferResult): boolean {
    if (offer) {
      return (
        this.funding.mensuality === offer.mensuality &&
        this.funding.duration === offer.duration &&
        this.funding.code === offer.code
      );
    }
  }

  protected async selectOffer(offer: LiteOfferResult) {
    try {
      this.loading = true;
      await this.storageService.selectOffer(offer.code, offer.duration);
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }
}
